// The Persian (Jalali) calendar, which the Persian interface shows everywhere. It rests on `Intl`'s own Persian
// calendar, so no table or library is involved, and every value sent to the server stays a Gregorian `Date`
const JALALI_LANG_CODES = new Set(['fa']);
const JALALI_PARTS = new Intl.DateTimeFormat('fa-u-ca-persian-nu-latn', {
  year: 'numeric', month: 'numeric', day: 'numeric',
});
// Farvardin 1 falls on 20 or 21 March, which is 621 or 622 years after the Jalali year in the Gregorian count
const GREGORIAN_YEAR_OFFSET = 621;
const NOWRUZ_EARLIEST_DAY = 20;
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_CORRECTIONS = 3;
const MONTHS_IN_YEAR = 12;
const DAYS_IN_WEEK = 7;
const JALALI_MONTH_NAME = new Intl.DateTimeFormat('fa-u-ca-persian', { month: 'long' });
const PERSIAN_NUMBER = new Intl.NumberFormat('fa', { useGrouping: false });
const PERSIAN_TWO_DIGITS = new Intl.NumberFormat('fa', { useGrouping: false, minimumIntegerDigits: 2 });
const YEARLESS_JALALI_YEAR = 1403;
const LEAP_DAY = { day: 29, month: 2 };
const MAX_BIRTHDAY_AGE = 150;

export type JalaliDate = { year: number; month: number; day: number };

export function isJalali(langCode?: string) {
  return Boolean(langCode && JALALI_LANG_CODES.has(langCode.split('-')[0]));
}

export function toJalali(date: Date): JalaliDate {
  const parts = JALALI_PARTS.formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)!.value);

  return { year: read('year'), month: read('month'), day: read('day') };
}

// Local noon of the Jalali day, so that no time zone moves it
export function fromJalali(year: number, month: number, day: number) {
  const dayOfYear = getJalaliDayOfYear(month, day);
  let date = new Date(year + GREGORIAN_YEAR_OFFSET, 2, NOWRUZ_EARLIEST_DAY + dayOfYear - 1, 12);

  for (let i = 0; i < MAX_CORRECTIONS; i++) {
    const found = toJalali(date);
    const yearShift = found.year === year ? 0 : (found.year < year ? 1 : -1);
    const shift = yearShift ? yearShift : dayOfYear - getJalaliDayOfYear(found.month, found.day);
    if (!shift) break;
    date = new Date(date.getTime() + shift * DAY_MS);
  }

  return date;
}

export function isSameDisplayYear(left: Date, right: Date, langCode?: string) {
  return isJalali(langCode)
    ? toJalali(left).year === toJalali(right).year
    : left.getFullYear() === right.getFullYear();
}

export function toDisplayYearMonth(date: Date, langCode?: string) {
  if (!isJalali(langCode)) return `${date.getFullYear()}-${date.getMonth()}`;

  const { year, month } = toJalali(date);
  return `${year}-${month - 1}`;
}

// A month of the displayed calendar, with `month` counted from 0 as `Date` does
export type DisplayMonth = { year: number; month: number };

export type MonthGrid = { prevMonthGrid: number[]; currentMonthGrid: number[]; nextMonthGrid: number[] };

export function getDisplayMonth(date: Date, langCode?: string): DisplayMonth {
  if (!isJalali(langCode)) return { year: date.getFullYear(), month: date.getMonth() };

  const { year, month } = toJalali(date);
  return { year, month: month - 1 };
}

export function addDisplayMonths({ year, month }: DisplayMonth, count: number): DisplayMonth {
  const index = year * MONTHS_IN_YEAR + month + count;
  const newYear = Math.floor(index / MONTHS_IN_YEAR);
  return { year: newYear, month: index - newYear * MONTHS_IN_YEAR };
}

export function compareDisplayMonths(left: DisplayMonth, right: DisplayMonth) {
  return (left.year - right.year) * MONTHS_IN_YEAR + left.month - right.month;
}

// Local midnight of a day of the displayed month
export function getDisplayDay({ year, month }: DisplayMonth, day: number, langCode?: string) {
  if (!isJalali(langCode)) return new Date(year, month, day);

  const date = fromJalali(year, month + 1, day);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatPersianNumber(value: number) {
  return PERSIAN_NUMBER.format(value);
}

export function formatPersianTwoDigits(value: number) {
  return PERSIAN_TWO_DIGITS.format(value);
}

export function getJalaliMonthName({ year, month }: DisplayMonth) {
  return JALALI_MONTH_NAME.format(fromJalali(year, month + 1, 1));
}

// The Persian week starts on Saturday
export function buildJalaliMonthGrid({ year, month }: DisplayMonth): MonthGrid {
  const firstDay = fromJalali(year, month + 1, 1);
  const leadingDays = (firstDay.getDay() + 1) % DAYS_IN_WEEK;
  const daysInMonth = getJalaliMonthLength(year, month + 1);
  const prevMonth = addDisplayMonths({ year, month }, -1);
  const daysInPrevMonth = getJalaliMonthLength(prevMonth.year, prevMonth.month + 1);

  const prevMonthGrid = Array.from({ length: leadingDays }, (_, i) => daysInPrevMonth - leadingDays + i + 1);
  const currentMonthGrid = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const trailingDays = (DAYS_IN_WEEK - ((leadingDays + daysInMonth) % DAYS_IN_WEEK)) % DAYS_IN_WEEK;
  const nextMonthGrid = Array.from({ length: trailingDays }, (_, i) => i + 1);

  return { prevMonthGrid, currentMonthGrid, nextMonthGrid };
}

export type BirthdayParts = { day: number; month: number; year?: number };

// A birthday without a year is read in Jalali 1403, a leap year. 1403 meets 20 March twice and 29 February never,
// so its 30 Esfand stands for 29 February, which keeps every day of both calendars on exactly one day of the other
export function toJalaliBirthday({ day, month, year }: BirthdayParts): BirthdayParts {
  if (year) return toJalali(new Date(year, month - 1, day, 12));
  if (month === LEAP_DAY.month && day === LEAP_DAY.day) return { day: 30, month: 12 };

  const nowruz = fromJalali(YEARLESS_JALALI_YEAR, 1, 1);
  const isBeforeNowruz = month - 1 < nowruz.getMonth() || (month - 1 === nowruz.getMonth() && day < nowruz.getDate());
  const jalali = toJalali(new Date(nowruz.getFullYear() + (isBeforeNowruz ? 1 : 0), month - 1, day, 12));
  return { day: jalali.day, month: jalali.month };
}

export function fromJalaliBirthday({ day, month, year }: BirthdayParts): BirthdayParts {
  if (!year && month === 12 && day === 30) return { ...LEAP_DAY };

  const date = fromJalali(year || YEARLESS_JALALI_YEAR, month, day);
  return { day: date.getDate(), month: date.getMonth() + 1, year: year ? date.getFullYear() : undefined };
}

// The day a yearless birthday is shown as under the Persian calendar
export function getYearlessJalaliDate(birthday: BirthdayParts) {
  const { day, month } = toJalaliBirthday(birthday);
  return fromJalali(YEARLESS_JALALI_YEAR, month, day);
}

export type BirthdayRange = { min: JalaliDate; max: JalaliDate };

// The server refuses a dated birthday in the future or more than 150 years back, counting in UTC, so the picker offers
// only the days inside that span by both the local and the UTC date. For the first hours of a day east of Greenwich,
// the server's today is still yesterday
export function getJalaliBirthdayRange(now: Date): BirthdayRange {
  const localDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const utcDay = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12);
  const [earlierDay, laterDay] = localDay <= utcDay ? [localDay, utcDay] : [utcDay, localDay];
  const earliest = new Date(laterDay.getFullYear() - MAX_BIRTHDAY_AGE, laterDay.getMonth(), laterDay.getDate(), 12);

  return { min: toJalali(earliest), max: toJalali(earlierDay) };
}

export function isJalaliBirthdayInRange({ day, month, year }: BirthdayParts, { min, max }: BirthdayRange) {
  if (!year) return true;

  const value = toSortKey({ year, month, day });
  return value >= toSortKey(min) && value <= toSortKey(max);
}

// Whether a month of the given year can hold any birthday of the range
export function isJalaliBirthdayMonthInRange(month: number, year: number | undefined, { min, max }: BirthdayRange) {
  if (!year) return true;

  return !(year === max.year && month > max.month) && !(year === min.year && month < min.month);
}

export function getJalaliBirthdayMonthLength(month: number, year?: number) {
  return getJalaliMonthLength(year || YEARLESS_JALALI_YEAR, month);
}

function toSortKey({ year, month, day }: JalaliDate) {
  return year * 10000 + month * 100 + day;
}

// Esfand has 30 days in a leap year, so its last day is read from the day before the next Nowruz
function getJalaliMonthLength(year: number, month: number) {
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  return toJalali(new Date(fromJalali(year + 1, 1, 1).getTime() - DAY_MS)).day;
}

// The first six months have 31 days and the next five 30
function getJalaliDayOfYear(month: number, day: number) {
  return month <= 6 ? (month - 1) * 31 + day : 186 + (month - 7) * 30 + day;
}
